/**
 * CropAssignmentPanel.tsx — Asigna cultivo (catálogo) y fecha de siembra desde la
 * pantalla Cultivo. Un lote con zonas se asigna zona por zona; uno sin zonas, el
 * lote completo. El mapa solo muestra lo asignado aquí.
 */
import { orderedModules } from '../../utils/module-tree';
import { useFieldErrors } from '../../hooks/useFieldErrors';
import { useState } from 'react';
import {
  makeStyles, tokens, Text, Button, Input, Spinner, Dropdown, Option, Field,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '../../i18n';
import { DateInput } from '../shared/DateInput';
import { fetchLoteCropCycles, startCropCycle, closeCropCycle, type CropCycle } from '../../api/crop.api';
import { defaultCycleLabel } from './assignedCrop';
import { CropCycleHistory } from './CropCycleHistory';
import { fetchCrops, fetchModules } from '../../api/terrain.api';
import type { Lote } from '../../types/api.types';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  newCrop: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'flex-end', flexWrap: 'wrap' },
});

interface RowProps {
  label: string;
  target: { targetType: 'MODULE' | 'LOTE'; targetId: string };
  open?: CropCycle;
  suggestedCiclo: string;
  canWrite: boolean;
  crops: { cropId: string; name: string }[];
  onChanged: () => void;
  /** 1 = zona hija: se muestra sangrada bajo su padre. */
  depth?: number;
}

const errorText = (e: unknown, fallback: string): string =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

function AssignmentRow({ label, target, open, suggestedCiclo, canWrite, crops, onChanged, depth = 0 }: RowProps) {
  const t = useT();
  const [cropId, setCropId] = useState('');
  const [date, setDate] = useState('');
  const [ciclo, setCiclo] = useState(suggestedCiclo);
  const [error, setError] = useState('');
  const fe = useFieldErrors();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const effectiveCiclo = ciclo.trim() || defaultCycleLabel(date || new Date().toISOString().slice(0, 10));

  const startMut = useMutation({
    mutationFn: () => startCropCycle({ ...target, cropId, ciclo: effectiveCiclo, ...(date ? { plantingDate: date } : {}) }),
    onSuccess: () => { setError(''); onChanged(); },
    onError: (e) => setError(errorText(e, t.crop.assignError)),
  });
  const closeMut = useMutation({
    mutationFn: () => closeCropCycle({ targetId: target.targetId, ciclo: open!.ciclo }),
    onSuccess: () => { setError(''); setConfirmOpen(false); setCropId(''); setDate(''); onChanged(); },
    onError: (e) => { setError(errorText(e, t.crop.removeCropError)); setConfirmOpen(false); },
  });

  return (
    <TableRow>
      <TableCell>
        <span style={{ paddingLeft: depth ? 20 : 0 }}>{depth ? '↳ ' : ''}{label}</span>
      </TableCell>
      <TableCell>
        {open ? <Text>{open.ciclo}</Text> : (
          <Input
            value={ciclo} onChange={(_, d) => setCiclo(d.value)} placeholder={effectiveCiclo}
            disabled={!canWrite || startMut.isPending}
            aria-label={t.crop.cycleOf.replace('{zone}', label)}
          />
        )}
      </TableCell>
      <TableCell>
        {open ? <Text>{crops.find(c => c.cropId === open.cropId)?.name ?? open.cropId}</Text> : (
          <Field {...fe.props('cropId')}>
            <Dropdown
              value={crops.find(c => c.cropId === cropId)?.name ?? ''}
              selectedOptions={cropId ? [cropId] : []}
              onOptionSelect={(_, d) => { setCropId(d.optionValue ?? ''); fe.clear('cropId'); }}
              disabled={!canWrite || startMut.isPending}
              aria-label={t.crop.assignCropLabel}
            >
              {crops.map(c => <Option key={c.cropId} value={c.cropId}>{c.name}</Option>)}
            </Dropdown>
          </Field>
        )}
      </TableCell>
      <TableCell>
        {open ? <Text>{open.plantingDate ?? '—'}</Text> : (
          <DateInput value={date} onValueChange={(v) => setDate(v)} disabled={!canWrite || startMut.isPending} aria-label={t.crop.assignDateLabel} />
        )}
      </TableCell>
      <TableCell>
        {canWrite && !open && (
          <Button
            size="small" appearance="primary" disabled={startMut.isPending}
            icon={startMut.isPending ? <Spinner size="tiny" /> : undefined}
            onClick={() => { if (fe.validate({ cropId: !cropId && t.common.validation.fieldRequired })) startMut.mutate(); }}
          >
            {t.crop.startCycle}
          </Button>
        )}
        {canWrite && open && (
          <Button size="small" appearance="subtle" aria-label={`${t.crop.closeCycle} ${label}`} onClick={() => setConfirmOpen(true)}>
            {t.crop.closeCycle}
          </Button>
        )}
        {error && <Text size={200} style={{ color: tokens.colorStatusDangerForeground1 }}> {error}</Text>}
        <Dialog open={confirmOpen} onOpenChange={(_, d) => setConfirmOpen(d.open)}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>{t.crop.closeCycleTitle}</DialogTitle>
              <DialogContent>{t.crop.closeCycleConfirm.replace('{zone}', label).replace('{ciclo}', open?.ciclo ?? '')}</DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setConfirmOpen(false)}>{t.common.cancel}</Button>
                <Button
                  appearance="primary" disabled={closeMut.isPending}
                  icon={closeMut.isPending ? <Spinner size="tiny" /> : undefined}
                  onClick={() => closeMut.mutate()}
                >
                  {t.crop.closeCycle}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      </TableCell>
    </TableRow>
  );
}

export function CropAssignmentPanel({ lote, canWrite, ciclo = '', onGoToCatalog }: { lote: Lote; canWrite: boolean; ciclo?: string; onGoToCatalog?: () => void }) {
  const t = useT();
  const s = useStyles();
  const qc = useQueryClient();

  const { data: crops = [] } = useQuery({ queryKey: ['crops-catalog'], queryFn: fetchCrops });
  const { data: modules = [], isLoading } = useQuery({
    queryKey: ['modules-by-lote', lote.loteId],
    queryFn: () => fetchModules({ loteId: lote.loteId }),
  });

  const { data: cycles = [] } = useQuery({
    queryKey: ['crop-cycles', lote.loteId],
    queryFn: () => fetchLoteCropCycles(lote.loteId),
  });
  const openOf = (targetId: string) => cycles.find(c => c.targetId === targetId && c.status === 'OPEN');
  const onChanged = () => {
    qc.invalidateQueries({ queryKey: ['crop-cycles', lote.loteId] });
    qc.invalidateQueries({ queryKey: ['lote-cycles', lote.loteId] });
    qc.invalidateQueries({ queryKey: ['modules-by-lote', lote.loteId] });
    qc.invalidateQueries({ queryKey: ['modules-map-at-date'] });
    qc.invalidateQueries({ queryKey: ['lotes-map'] });
    qc.invalidateQueries({ queryKey: ['modules'] });
    qc.invalidateQueries({ queryKey: ['lotes'] });
  };

  return (
    <div className={s.root}>
      <Text weight="semibold">{t.crop.assignTitle}</Text>
      <Text size={200}>{modules.length > 0 ? t.crop.assignHintZones : t.crop.assignHintLote}</Text>

      {crops.length === 0 && (
        <div className={s.newCrop}>
          <Text size={200}>{t.crop.assignNoCatalog}</Text>
          {onGoToCatalog && <Button size="small" onClick={onGoToCatalog}>{t.crop.assignGoCatalog}</Button>}
        </div>
      )}

      {isLoading ? <Spinner size="tiny" /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{modules.length > 0 ? t.crop.assignZoneCol : t.crop.loteLabel}</TableHeaderCell>
              <TableHeaderCell>{t.crop.cicloLabel}</TableHeaderCell>
              <TableHeaderCell>{t.crop.assignCropLabel}</TableHeaderCell>
              <TableHeaderCell>{t.crop.assignDateLabel}</TableHeaderCell>
              <TableHeaderCell />
            </TableRow>
          </TableHeader>
          <TableBody>
            {modules.length === 0 ? (
              <AssignmentRow
                key={`${lote.loteId}:${openOf(lote.loteId)?.ciclo ?? ''}`} label={lote.name}
                target={{ targetType: 'LOTE', targetId: lote.loteId }} open={openOf(lote.loteId)}
                suggestedCiclo={ciclo} canWrite={canWrite} crops={crops} onChanged={onChanged}
              />
            ) : orderedModules(modules).map(({ mod: m, depth }) => (
              <AssignmentRow
                depth={depth}
                key={`${m.moduleId}:${openOf(m.moduleId)?.ciclo ?? ''}`} label={m.name || m.moduleCode}
                target={{ targetType: 'MODULE', targetId: m.moduleId }} open={openOf(m.moduleId)}
                suggestedCiclo={ciclo} canWrite={canWrite} crops={crops} onChanged={onChanged}
              />
            ))}
          </TableBody>
        </Table>
      )}

      <CropCycleHistory lote={lote} modules={modules} crops={crops} />
    </div>
  );
}
